/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  addIframeSrcParams,
  addQueryParam,
  cleanQuery,
  getPaths,
  getPathWithoutTenant,
  getQueryParams,
  getQueryValue,
  getTenantAndPath,
  getUpdatedPath,
  hasChanges,
  hashToPath,
  splitPath,
  updatePath
} from '@exc/url';
import {AuthInstance} from '@exc/auth';
import {classNames} from '@exc/dom';
import CoreContext from '../context';
import discoveryUtil from '../utils/discovery';
import {envSources} from '../utils/config';
import EventObserver from './EventObserver';
import {getConfiguration} from '../utils/config';
import {getPerformanceMarks, isOrgChange} from '../utils/utils';
import {getPulseServiceFactory, NotFound} from '../imports/ImportManager';
import {history} from '@exc/router';
import Iframe from './Iframe';
import metrics, {Level, Events as MetricsEvents, MetricsRuntime} from '@adobe/exc-app/metrics';
import PropTypes from 'prop-types';
import React from 'react';
import Wait from '@react/react-spectrum/Wait';
import './Sandbox.css';

const scopedEvents = {
  helpCenter: 'CUSTOM_HELP_BUTTON_CLICK',
  userProfile: 'CUSTOM_PROFILE_BUTTON_CLICK'
};

let firstRender = true;

export default class Sandbox extends React.Component {
  constructor(props, context) {
    super(props, context);

    const authInfo = {
      accessToken: context.adobeIMS.getAccessToken()
    };
    let env = context.environment;
    const {history, ims, thirdParty} = props.config.sandbox;
    const {type, config} = history || {};
    const queryParams = getQueryParams();

    // Add the module runtime src
    this.runtimeScript = document.getElementById(`runtimeScript`);
    if (!this.runtimeScript) {
      throw new Error('Missing Module Runtime src');
    }
    this.thirdParty = thirdParty;

    this.getPulseService = async () => {
      const {getPulseService} = await getPulseServiceFactory();
      return getPulseService({authInfo, env, queryParams});
    };
    this.metrics = metrics.create('exc.core.modules.Sandbox');
    this.context.setSolutionConfig(props.config);

    this.customIms;
    if (ims) {
      this.customIms = ims[env] || {
        client_id: ims.client_id,
        scope: ims.scope
      };
    }

    // Reset viewport takeover for each solution. One solution may support it
    // while another may not. Need to make sure the classes are up-to-date to
    // ensure no weirdness.
    this.context.setConfigStates({supportsViewportTakeover: false});
    this.unloadPromptMessage = this.context.formatMessage({
      customLocale: this.context.locale,
      defaultMessage: 'Are you sure?',
      messageId: 'unloadPromptMessage'
    });

    const {appId, metricsAppId, sandbox} = props.config;
    this.metricsAppId = metricsAppId || appId;

    this.frame = null;
    this.initialized = false;
    this.lastPayload = null;
    this.historyType = type || history;
    this.historyConfig = config || {};
    this.paramsChangedOnUpdate = false;

    this.onMessage = this.onMessage.bind(this);
    this.onMount = this.onMount.bind(this);
    this.openRequests = [];
    this.queuedDataEvents = [];
    this.state = {
      discovery: false,
      loaded: false,
      main: false,
      pageTimeout: false,
      spinner: !sandbox.hideInitialSpinner,
      src: null
    };
    this._discoveryTime = 0;

    this.sandboxTimer = this.metrics.start('Sandbox', {applicationId: this.metricsAppId});
    this.eventObserver = new EventObserver();
    this.eventObserver.on('keyboard', this.handleUserInputEvent);
    this.eventObserver.on('click', this.handleUserInputEvent);
    this.eventObserver.on('mouseup', this.handleUserInputEvent);
    const {discoveryCache, endpoints} = getConfiguration({environment: this.context.environment});
    this.apiGatewayUrl = endpoints.apiGateway;
    this.discoveryCache = discoveryCache;

    window.adobeMetrics.setApplication({
      solution: {
        id: this.metricsAppId,
        version: window.config && window.config.spaVersion
      }
    });

    this.setPageTimeout();
  }

  setPageTimeout() {
    const {pageTimeout} = this.props.config.sandbox;
    const localConfigs = JSON.parse(window.localStorage.getItem('unifiedShellConfig')) || {};
    const devmodeEnabled = localConfigs.devmodeEnabled === true || getQueryValue('devmode') === 'true';
    if (this.context.environment === 'dev' || devmodeEnabled) {
      return;
    }

    const shortTimeoutEnvs = ['stage', 'prod'];
    const DEFAULT_TIMEOUT = shortTimeoutEnvs.includes(this.context.environment) ? 20000 : 90000;
    const timeout = pageTimeout || DEFAULT_TIMEOUT;
    this.loadTimeout = setTimeout(() => {
      this.setState({pageTimeout: true});
      this.metrics.event(
        'Sandbox.pageTimeout',
        `Solution loading timed out at ${timeout}ms`,
        {
          level: Level.ERROR,
          solution: {
            id: this.metricsAppId
          },
          url: this.state.src
        }
      );
    }, timeout);
  }

  /**
   * Waiting for IMS to return in the case of a discovery URL.
   * Two cases: (1) discovery URL (2) templated static URL
   */
  shouldWaitForIms(src) {
    let urlNeedsImsInfo = (typeof src === 'object') || (typeof src === 'string' && src.match(/{[A-Za-z]+}/g));
    return !this.context.imsProfile && urlNeedsImsInfo;
  }

  getSourceEnv() {
    let env = this.context.environment;
    const {appId, spaAppId, sandbox} = this.props.config;
    const srcs = sandbox.sources;
    env = getQueryValue('source') || env;

    // If SpaAppId exists, set the config URL for the respective environment
    if (env && spaAppId) {
      if (!srcs[env]) {
        srcs[env] = envSources[env] + spaAppId;
      } else if (env !== 'dev') {
        this.metrics.info(`No default config found for ${spaAppId}`);
      }
    }
    if (!env || !srcs[env]) {
      this.metrics.error(`No matching configuration source for env ${env} in app ${appId}`);
      this.setState({notFound: true});
      return;
    }
    return srcs[env];
  }

  async generateIframeSource(forceReload) {
    this.updateBasePath(this.props);
    const {appId, spaAppId} = this.props.config;
    const {absolutePaths, hashType} = this.historyConfig;
    const isHashType = this.historyType === Sandbox.HISTORY_TYPES.HASH;
    const pathType = isHashType ? 'hash' : 'pathname';

    const {host, origin} = window.location;
    let src = this.getSourceEnv();

    if (!src) {
      return;
    }

    // Waiting for IMS to return in the case of a discovery URL.
    // In the case of a regular source, let's proceed!
    if (this.shouldWaitForIms(src)) {
      return;
    }

    let discovery;
    let discoveryFromCache = false;
    let discoveryHasUrl = false;
    let fromDiscovery = false;
    let iframeTimer = this.metrics.start('IframeTimer');

    if (typeof src === 'string') {
      src = new URL(this.updateTemplatedURL(src));
    } else if (typeof src === 'object') {
      // Restart the timeout countdown for discovery
      clearTimeout(this.loadTimeout);
      this.setPageTimeout();
      fromDiscovery = true;
      discovery = this.discoveryCache && discoveryUtil.get(appId);
      discoveryFromCache = !!discovery;
      try {
        src = {...src, discovery: this.updateTemplatedURL(src.discovery)};
        discovery = discovery || await this.fetchDiscoveryUrl(src.discovery, src.payload || [], src.withCredentials, src.xApiKey);

        if ('url' in discovery) {
          discoveryHasUrl = true;
          src = new URL(discovery.url);
        } else if ('url' in src) {
          src = new URL(this.updateTemplatedURL(src.url, {...src, ...discovery}));
        } else {
          throw new Error('Discovery must either return a URL, or contain a URL in the configuration');
        }

        // Custom IMS client and scope is returned!
        if (discovery.client_id && discovery.scope) {
          this.customIms = {
            client_id: discovery.client_id,
            scope: discovery.scope
          };
        }
        discoveryFromCache && (src[pathType] = discovery.rootPath || '/');
        this.metrics.event('Sandbox.discoveryURL', {discovery, discoveryFromCache, src});
      } catch (error) {
        this.metrics.error(error.message);
        this.setState({notFound: true});
        return;
      }

      clearTimeout(this.loadTimeout);
      this.initialized || this.setPageTimeout();
    }
    this.configURL = new URL(src);
    this.origin = src.origin;

    const mrSrc = new URL(this.runtimeScript.getAttribute('data-src') || this.runtimeScript.src, origin);

    // SPA Pipeline apps should always get Runtime from the same domain as Shell.
    const runtimeSource = spaAppId ? this.runtimeScript.src : mrSrc.toString();
    addQueryParam(src.searchParams, '_mr', runtimeSource);
    addQueryParam(src.searchParams, 'shell_domain', host);

    // If discovery was used and is not from the cache, we should have the
    // entire URL, so we can just set the src and stop. If the discovery
    // response didn't have the url (came from config), or if the discovery came
    // from cache, we need to pull all the path and query params from the
    // current URL so that it's on the correct page.
    if (discovery && discoveryHasUrl && !discoveryFromCache) {
      forceReload && src.searchParams.set('shell_forceReload', Date.now());
      this._discoveryTime = iframeTimer.time('Loaded.Discovery.NotCache', {url: src});
      return this.setState({discovery: fromDiscovery, src});
    }

    addIframeSrcParams(src, this.runtimeScript);

    // Append the correct path from the outer frame (useful for deep linking)
    // This should correct to the "right" url (eg the page that is initially
    // loaded) if our src is just the base (eg '/').
    const params = this.props.params[1] || '';
    if (params) {
      const path = hashType === 'noslash' ? params : `/${params}`;
      if (absolutePaths) {
        src[pathType] = path;
      } else {
        src[pathType] += path;
      }
      // Replacing any duplicate slashes (e.g. //something) with a single slash
      // so that the URL is not broken.
      src[pathType] = src[pathType].replace(/([/]+)/g, '/');
    }
    // Pass all non-config query params through to the iframe.
    const url = new URL(`${origin}${hashToPath()}`);
    // If the history type isn't hash, the hash should be set.
    if (!isHashType) {
      src.hash = url.hash;
    }

    this.origin = src.origin;
    sessionStorage.setItem('prevSrc', JSON.stringify({
      browser: window.location.href,
      configUrl: this.configURL.toString(),
      iframe: src
    }));

    forceReload && src.searchParams.set('shell_forceReload', Date.now());
    this._discoveryTime = iframeTimer.time('Loaded', {url: src});
    this.setState({discovery: fromDiscovery, src});

    // Clear page timeout since discovery is done
    clearTimeout(this.loadTimeout);

    // If app not yet initialized, start countdown again
    if (!this.initialized) {
      this.setPageTimeout();
    }
  }

  /**
   * Fetch URL from solution config endpoint.
   * @param {string} url The endpoint for fetching the discovery URL.
   * @param {boolean} withCredentials Whether to send the request withCredentials flag.
   * @returns {string} String representing the solution's base URL.
   */
  async fetchDiscoveryUrl(url, properties = [], withCredentials = false, xApiKey = false) {
    const {activeProductContext, adobeIMS, imsClientId, imsOrg, imsOrgs, imsProfile, locale, subOrg, tenant} = this.context;
    const accessToken = adobeIMS.getAccessToken();
    const currentUrl = hashToPath();
    // Path is equal to URL hash minus basePath
    const fullPath = getPathWithoutTenant(currentUrl);
    const path = getPathWithoutTenant(currentUrl.replace(this.basePath, ''));

    function discoveryReq() {
      return new Promise((resolve, reject) => {
        let xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.onreadystatechange = () => {
          if (xhr.readyState === 4) {
            if (/2\d\d/.test(xhr.status)) {
              try {
                resolve(JSON.parse(xhr.responseText));
              } catch (e) {
                reject(e);
              }
            } else {
              reject();
            }
          }
        };
        xhr.onerror = () => reject();

        if (withCredentials) {
          xhr.withCredentials = true;
        }
        xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
        xhr.setRequestHeader('Content-Type', 'application/json');

        if (xApiKey) {
          xhr.setRequestHeader('x-api-key', imsClientId);
        }

        const payload = {
          activeProductContext,
          fullPath,
          imsOrg,
          imsOrgs,
          imsProfile,
          locale,
          path,
          subOrg,
          tenant
        };

        properties.length && Object.keys(payload)
          .filter(key => !properties.includes(key))
          .forEach(key => (delete payload[key]));

        xhr.send(JSON.stringify(payload));
      });
    }

    let discoveryTimer = this.metrics.start('Discovery');
    try {
      const {appId} = this.props.config;
      const data = await discoveryReq();
      discoveryTimer.time('success', {
        appId,
        imsOrg,
        path,
        response: data,
        subOrg,
        tenant,
        url
      });
      discoveryUtil.set(appId, data);
      return data;
    } catch (error) {
      discoveryTimer.time('error', {url});
      this.metrics.error(`Failed to fetch url: ${error.message}`);
      throw new Error('Failed to fetch url ', error);
    }
  }

  updateBasePath(props) {
    let {params, path} = props;
    let {path: justPath, tenant} = getTenantAndPath(path);
    this.basePath = justPath.replace(params[1] || '', '').replace(/\/$/, '');
    // Ensure correct base path if switching between tenanted and tenantless
    if (tenant && !this.props.config.hideTenant) {
      this.basePath = `/@${tenant}${this.basePath}`;
    }
    this.context.setConfigStates({basePath: this.basePath});
  }

  updateTemplatedURL(urlString, extraParams = {}) {
    const templateStrings = urlString.match(/{[A-Za-z0-1]+}/g);
    const url = new URL(window.location.href);
    if (templateStrings) {
      templateStrings.forEach(string => {
        let contextString = string.substring(1, string.length - 1);
        let replacement =
          this.context[contextString] ||
          url.searchParams.get(contextString) ||
          this.props.params[contextString] ||
          extraParams[contextString] ||
          (extraParams.defaults || {})[contextString];
        if (replacement) {
          urlString = urlString.replace(string, replacement);
        }
      });
    }
    return urlString;
  }

  getNextConfig() {
    const {hash} = window.location;
    const match = this.props.router.match(hashToPath({hash}));
    if (!match) {
      return false;
    }
    const children = (match.props || {}).children;
    if (!children) {
      return undefined;
    }
    return (children.props || {}).config;
  }

  componentWillUnmount() {
    const {appId} = this.props.config;
    const nextAppId = (this.getNextConfig() || {}).appId;

    this.eventObserver.unwatch();
    this.frame = null;
    this.customFeedback = false;

    this.context.resetConfigStates(appId, nextAppId);
    this.blockNavigation(false);
    clearTimeout(this.loadTimeout);
  }

  generateSourceOnReload() {
    const src = this.getSourceEnv();

    if (!src || this.shouldWaitForIms(src)) {
      return;
    }

    // If caused by refresh and use iframe src from local storage
    if (this.props.config.appId === 'analytics' && sessionStorage.getItem('prevSrc')) {
      const prevSrc = JSON.parse(sessionStorage.getItem('prevSrc'));
      if (window.location.href === prevSrc.browser) {
        const iframeSrc = new URL(prevSrc.iframe);
        this.configURL = new URL(prevSrc.configUrl);
        this.origin = iframeSrc.origin;
        // add the params here
        this.setState({src: iframeSrc});
        return;
      }
    }
    this.generateIframeSource();
  }

  componentDidMount() {
    this.eventObserver.watch();
    this.generateSourceOnReload();
  }

  shouldComponentUpdate(nextProps) {
    const getParam = p => (Object.keys(p).length ? p[1] : '') || '';

    const {hash, params} = nextProps;
    const query = cleanQuery(nextProps.query);
    const nextParams = getParam(params);
    const prevParams = getParam(this.props.params);

    if (prevParams === nextParams && JSON.stringify(this.props.query) === JSON.stringify(query) && this.props.hash === hash) {
      this.paramsChangedOnUpdate = false;
      return true;
    }

    this.paramsChangedOnUpdate = true;
    const {spinnerOnChange} = this.historyConfig;
    if (this.historyType === Sandbox.HISTORY_TYPES.SERVER && spinnerOnChange) {
      this.setState({spinner: true});
    }
    this.updateBasePath(nextProps);
    this.sendMessage({
      type: Sandbox.EVENTS.HISTORY,
      value: nextParams + (nextProps.querystring ? `?${nextProps.querystring}` : '') + (hash ? `#${hash}` : '')
    });

    return true;
  }

  getSnapshotBeforeUpdate() {
    let snapshot = this.prevContext || null;
    this.prevContext = this.context;
    return snapshot;
  }

  hasConfigChanges(prevContext, keys) {
    return prevContext && keys.some(key => prevContext[key] !== this.context[key]);
  }

  componentDidUpdate(prevProps, prevState, prevContext) {
    // The path changed (& fired a history event in shouldComponentUpdate) & Not Found was previously active.
    // We should hide Not Found since the page navigated and may not be needed now.
    this.paramsChangedOnUpdate && this.state.notFound && this.setState({notFound: false});

    // Regenerate iframe source when IMS profile is known.
    // This is the case when discovery URL needs to be invoked.
    // Otherwise, dont need to do this since IMS profile isn't needed to start loading.
    if (prevContext && prevContext.imsProfile === null && this.context.imsProfile) {
      this.queuedDataEvents.forEach(fn => fn());
      this.queuedDataEvents = [];
      return this.generateSourceOnReload();
    }

    // Don't send a configuration update if nothing has changed, or the src has not been set
    if (!this.state.src || JSON.stringify(prevContext) === JSON.stringify(this.context)) {
      return;
    }

    // Send event if Coach Marks position updated in Core state
    this.hasConfigChanges(prevContext, ['coachMarkPosition']) && this.sendMessage({
      type: Sandbox.EVENTS.COACH_MARK_POSITIONED,
      value: this.context.coachMarkPosition
    });

    // Send event if NPS score changed
    this.hasConfigChanges(prevContext, ['npsScore']) && this.sendMessage({
      type: Sandbox.EVENTS.NPS,
      value: this.context.npsScore
    });

    // If the org or subOrg have changed, clear the discovery cache.
    this.hasConfigChanges(prevContext, ['imsOrg', 'subOrg']) && discoveryUtil.clear();

    // If refreshOnImsChange is set and IMS has changed, refresh iframe source.
    if (this.props.config.refreshOnImsChange && this.hasConfigChanges(prevContext, ['imsOrg'])) {
      return this.generateIframeSource(true);
    }
    this.sendConfiguration();
    this.context.customSearch.enabled && this.sendCustomSearchValue();
  }

  /**
   * Handles the case where data has been requested before it is available.
   * Ensures that any remaining requests for data are fulfilled.
   */
  processOpenRequests() {
    const payloadValue = (this.lastPayload || {}).value || {};
    this.openRequests.forEach(key => {
      if (key in payloadValue) {
        this.sendDataResponse(key, key);
      }
    });
  }

  /**
   * Constructs baseUrl using basePath.
   * @param {string} basePath The base path of the url.
   * @returns {string} The full base url.
   */
  setBaseUrl(basePath) {
    let baseUrl = new URL(window.location.origin);
    baseUrl.hash = basePath;
    baseUrl.search = window.location.search;
    return baseUrl.toString().replace(/\/$/, '');
  }

  getConfigurePayload() {
    let {
      activeProductContext,
      adobeIMS,
      environment,
      imsClientId,
      imsEnvironment,
      imsOrg,
      imsOrgs,
      imsOrgName,
      imsProfile,
      locale,
      metricsEnv,
      preferredLanguages,
      shellHeight,
      shellInfo,
      subOrg,
      tenant
    } = this.context;
    let {analytics, appId, sandbox} = this.props.config;
    let {code: solution, scripts = {}} = analytics || {};
    let env = getQueryValue('source') || environment;
    analytics = scripts[env] ? {script: scripts[env], solution} : null;
    let baseUrl = this.setBaseUrl(this.basePath);
    let imsToken = adobeIMS.getAccessToken();
    let metricsConfig = MetricsRuntime.configure();

    return {
      type: Sandbox.EVENTS.CONFIGURE,
      value: {
        activeProductContext,
        analytics,
        apiGatewayUrl: this.apiGatewayUrl,
        appId,
        baseFrameUrl: this.configURL.toString(),
        baseUrl,
        discovery: this.state.discovery,
        environment,
        externalQueryParams: window.location.search,
        historyType: this.historyType,
        hosts: sandbox.hosts,
        imsClientId,
        imsEnvironment,
        imsOrg,
        imsOrgName,
        imsOrgs,
        imsProfile,
        imsToken,
        locale,
        metricsAppId: this.metricsAppId,
        metricsConfig,
        metricsEnv,
        preferredLanguages,
        shellHeight,
        shellInfo,
        subOrg,
        tenant,
        tenantAppId: this.props.config.tenantAppId
      }
    };
  }

  /**
   * This functon is handler for all events happening on the shell.
   * @param {Object} value The payload.
   */
  handleUserInputEvent = value => {
    if (this.customFeedback && value.type === 'click' && (value.data || {}).className === 'spectrum-Shell-feedback-button') {
      this.sendMessage({type: Sandbox.EVENTS.CUSTOM_FEEDBACK, value});
    }
    this.sendMessage({type: Sandbox.EVENTS.USER_INPUT, value});
  }

  onMount(frame) {
    this.frame = frame;
    this.sandboxTimer.time('init', {
      discovery: this.state.discovery,
      discoveryTime: this._discoveryTime
    });
    this.setState({loaded: true});
  }

  /**
   * Determines which base to use for the path based on whether the
   * `base` property is defined in the route config. This will be the default
   * instead of `this.basePath` unless it's not defined. This is for cases when
   * multiple configs are needed but need a single base. For example, Target has
   * 5 route configs (`/target/audiences`, etc) but really only want `/target`
   * to be the base.
   * @returns {string} The base path to use.
   */
  getBase() {
    const {base} = this.props.config;
    return base ? getUpdatedPath(this.basePath, base) : this.basePath;
  }

  /**
   * To be run on page load. Runs on every server history change.
   * @param {Object} obj The below params.
   * @param {string} obj.configuration Solution configuration.
   * @param {string} obj.hash Hash from iframe.
   * @param {string} obj.pathname Pathname of iframe.
   * @param {string} obj.search Search from iframe.
   */
  async onReady({configuration, hash, href, pathname, search}) {
    this.updateBasePath(this.props);

    let {router} = this.props;

    // In the "server" case (meaning, traditional server rendered app)
    // we want to redirect to the correct url if not already served on ready
    if (this.historyType === Sandbox.HISTORY_TYPES.SERVER) {
      // Get and combine paths and search strings
      let {absolutePaths} = this.historyConfig;
      let {browser, iframe} = getPaths(pathname, search, hash, !!absolutePaths, this.basePath, this.configURL);
      let browserString = browser.path + browser.search + browser.hash;
      let iframeString = iframe.path + iframe.search + iframe.hash;
      // Compare the iframe's path with the current browser path
      if (hasChanges(browserString, iframeString).any) {
        // If not matching, update the browser's path to match iframe path.
        // Additionally, protect against multiple // in a row.
        router.trigger((this.basePath + iframeString).replace(/^\/{2,}/g, '/'), true);

        // The server history type does not send updates with page changes, so
        // the page href needs to be handled here.
        this.trackCurrentIframeSrc(href);
      }
    }

    this.sandboxTimer.time('connected', `Solution loaded: ${this.props.config.appId}`, {
      appId: this.props.config.appId,
      configuration,
      customIms: this.customIms,
      hash,
      href,
      pathname,
      search
    });

    // For backwards compatibility, set the customIms instance variable
    // if the ims property was provided in the runtime config.
    let {ims} = configuration || {};
    this.customIms = this.customIms || ims;

    this.customIms && await this.getOrRefreshIms(this.customIms);
    this.initialized = true;

    // Clear the page timeout
    clearTimeout(this.loadTimeout);
    this.sendConfiguration(true);
  }

  trackCurrentIframeSrc(href) {
    let iframeSrc = new URL(href);
    addIframeSrcParams(iframeSrc, this.runtimeScript);
    sessionStorage.setItem('prevSrc', JSON.stringify({
      browser: window.location.href,
      configUrl: href,
      iframe: iframeSrc
    }));
  }

  async reAuthIMS(clientId, scope) {
    // This should always pick up the instance as opposed to creating a new
    // instance. If this changes, will need to pass configuration into this.
    const authInstance = await AuthInstance();
    const res = await authInstance.fetchProfileAndAccessTokenInfo(clientId, scope);
    const {access_token, ...profile} = res;
    return {profile, token: access_token};
  }

  /**
   * Retrieves the token from cache or fetches the token for
   * the provided client_id and scopes via IMS if it's not in the cache.
   * @param {Object} obj Information.
   * @param {string} obj.client_id Custom IMS client ID.
   * @param {string} obj.scope Custom IMS scopes assigned to the client_id.
   * @returns {Promise} IMS information.
   */
  async getOrRefreshIms({client_id, scope}) {
    let cachedInfo = this.context.getCachedToken(client_id, scope);

    // If the cached token is available, return it.
    if (cachedInfo) {
      return cachedInfo;
    }

    // Need to asynchronously fetch the token from IMS for the custom client id.
    // Upon completion, sends the payload if one is queued.
    let newIms = await this.reAuthIMS(client_id, scope);
    this.context.updateCachedTokens(client_id, scope, newIms);
    return newIms;
  }

  async sendConfiguration(force = false) {
    // Only send configuration payloads if it's initialized and there is
    // a valid profile.
    if (!force && !this.initialized || !this.context.imsProfile || this.configPending) {
      // If solution is not yet ready but Shell is (not initialized and imsProfile available)
      // send a sendConfiguration event with solutionReady false.
      if (!this.initialized && !this.configPending && this.context.imsProfile) {
        this.sandboxTimer.time('sendConfiguration', {solutionReady: false});
      }
      return;
    }

    let imsData = {};

    if (this.customIms) {
      this.configPending = true;
      let {profile: imsProfile, token: imsToken} = await this.getOrRefreshIms(this.customIms);
      Object.assign(imsData, {imsClientId: this.customIms.client_id, imsProfile, imsToken});
    }

    let payload = this.getConfigurePayload();
    Object.assign(payload.value, imsData);
    payload.changed = this.getChangedConfig(payload.value, (this.lastPayload || {}).value || {});
    const firstSend = !this.lastPayload;
    this.lastPayload = payload;
    this.sendMessage(payload);
    this.processOpenRequests();
    this.configPending = false;
    this.sandboxTimer.time('sendConfiguration', {firstSend, solutionReady: true});
  }

  sendCustomSearchValue() {
    let searchState = this.context.customSearch.open;
    // Only send event if the state of the search menu has changed
    if (searchState !== this.previousSearchState) {
      this.sendMessage({
        type: Sandbox.EVENTS.CUSTOM_SEARCH,
        value: searchState
      });
      this.previousSearchState = searchState;
    }
  }

  getChangedConfig(payload, lastPayload) {
    // On first config event, mark all values as changed
    const keys = Object.keys(payload);
    if (!lastPayload) {
      return keys;
    }
    return keys.filter(key => JSON.stringify(payload[key]) !== JSON.stringify(lastPayload[key]));
  }

  async sendDataResponse(type, value) {
    this.metrics.event('Sandbox.sendDataResponse', {metricsAppId: this.metricsAppId}, {type}, {value});
    let updatedContext = {};
    if (this.customIms) {
      let {profile: imsProfile, token: imsToken} = await this.getOrRefreshIms(this.customIms);
      Object.assign(updatedContext, this.context, {imsClientId: this.customIms.client_id, imsProfile, imsToken});
    }

    let payload;
    switch (type) {
      case 'getApp':
        let {landingpage, services, solutions} = this.context.shellInfo;
        let app = services.concat(solutions).concat(landingpage).find(l => l.appId === value);
        payload = {[`${type}_${value}`]: app};
        break;
      default:
        let hasContextKey = type in updatedContext || type in this.context;
        if (hasContextKey) {
          payload = {[type]: updatedContext[type] || this.context[type]};
        }
    }

    payload && this.sendMessage({
      type: Sandbox.EVENTS.DATA_FULFILL,
      value: payload
    });

    this.openRequests.splice(this.openRequests.indexOf(type), 1);
  }

  sendMessage(msg) {
    this.frame && this.frame.contentWindow && this.frame.contentWindow.postMessage({...msg, unifiedShell: 1}, this.origin);
  }

  onTitleChange(newTitle) {
    let divider = ' | ';
    let parts = document.title.split(divider);
    let end = parts[parts.length - 1];
    document.title = newTitle + divider + end;
  }

  onMessage(ref, message) {
    let {appId, type, value, unifiedShell} = message.data || {};
    let {EVENTS} = Sandbox;

    // Only accept messages from unified shell code
    if (unifiedShell !== 1) {
      return;
    }

    // If loading a third party solution, check if a supported event.
    if (this.thirdParty && !(type in Sandbox.EVENTS_THIRDPARTY)) {
      this.metrics.event(`Unsupported 3rd party event ${type}`);
      return;
    }

    // Updates the origin to ensure it's the current one that is being used.
    // Analytics changes origins and post messaging won't work if we don't
    // update to the newest one.
    if (type === EVENTS.PING) {
      const {appId} = this.props.config;
      this.origin = message.origin;
      this.metrics.event('Sandbox.onMessage.ping', {appId});
      // Return empty message to initiate origin
      this.sendMessage({appId, type: EVENTS.PONG});
      this.props.onMessage(type, value);
      return;
    }

    // Ignore events that come from a different iframe than what is currently loaded
    if (appId !== this.props.config.appId) {
      this.metrics.info(
        `Post message appId ${appId} does not match current appId ${this.props.config.appId}`,
        {message}
      );
    }

    // Handle each runtime event in the events queue
    if (type === EVENTS.QUEUE) {
      let historyEvents = [];
      value = value.filter(v => {
        if (v[0] === EVENTS.HISTORY) {
          historyEvents.push(v);
          return false;
        }
        return true;
      });

      if (historyEvents.length) {
        value.push(historyEvents[historyEvents.length - 1]);
      }

      // Ignore CLICK event if this is an OPEN_HELP_CENTER event
      if (value.find(v => v[0] === EVENTS.OPEN_HELP_CENTER)) {
        value = value.filter(v => v[0] !== EVENTS.CLICK);
      }

      value.forEach(([key, val]) => this.handlePostMessage(key, val));
    }
  }

  unloadListener = (e) => {
    e.preventDefault();
    e.returnValue = this.unloadPromptMessage;
  }

  blockNavigation = (enabled, options) => {
    if (!enabled) {
      window.removeEventListener('beforeunload', this.unloadListener);
      this.unblock && this.unblock();
      return;
    }
    this.unblock = history.block((location, action) => {
      if (action !== 'REPLACE') {
        let promptUser = (options && options.onOrgChangeOnly) ?
          isOrgChange(history.location.pathname, location.pathname) : true;
        if (promptUser) {
          return this.unloadPromptMessage;
        }
      }
    });
    window.addEventListener('beforeunload', this.unloadListener);
  }

  handlePostMessage(type, value) {
    let {EVENTS} = Sandbox;
    switch (type) {
      case EVENTS.AUTH_EXPIRATION:
        this.context.onSignOut(true, 'User timed out');
        break;
      case EVENTS.BLOCK_NAVIGATION: {
        // Prevent navigation blocking for tenantless solutions since it is not supported
        const {hideTenant} = this.props.config;
        value && !hideTenant && this.blockNavigation(value.enabled, value.options);
        break;
      }
      case EVENTS.CAAS_LOAD:
        this.context.loadCaas(() => this.sendMessage({type: EVENTS.CAAS_READY}));
        break;
      case EVENTS.CAN_TAKEOVER:
        this.context.setConfigStates({supportsViewportTakeover: value});
        break;
      case EVENTS.CUSTOM_HERO_CLICK:
        // Disable for all but legacy apps, everyone else should be using hero.path or basePath
        if (this.context.solutionType === 'application') {
          this.context.setConfigStates({customHeroClick: () => this.sendMessage({type: EVENTS.HERO_CLICK})});
        }
        break;
      case EVENTS.CLICK:
        document.dispatchEvent(new CustomEvent('sandboxClick'));
        break;
      case EVENTS.DATA_REQUEST:
        const {type: typ, value: val} = value;
        this.openRequests.push(typ);
        (!this.lastPayload || !Object.keys(this.lastPayload).length) || this.sendDataResponse(typ, val);
        break;
      case EVENTS.DONE: {
        let metricsInfo = {applicationId: this.metricsAppId};
        if (firstRender) {
          Object.assign(metricsInfo, getPerformanceMarks(this.state.src, value && value.iframePerformance));
          firstRender = false;
        }
        this.sandboxTimer.time({event: MetricsEvents.PAGE_LOAD_DONE}, this.metricsAppId, metricsInfo);
        this.context.setConfigStates({solutionLoaded: true});
        this.setState({main: value.addMainElement, spinner: false});
        break;
      }
      case EVENTS.EXC_PULSE:
        this.pulseHandler(value);
        break;
      case EVENTS.EXC_SETTINGS:
        this.settingsHandler(value);
        break;
      case EVENTS.FOCUS:
        document.dispatchEvent(new CustomEvent('sandboxClick'));
        break;
      case EVENTS.HISTORY: {
        // Hash history modifies the provided path to include the hash as part
        // of the path. For example, if `value.path` is `#something`, the
        // updated value will be `/something`. This keeps the URL tidy.
        if (this.historyType === Sandbox.HISTORY_TYPES.HASH) {
          const url = new URL(`${window.location.origin}${value.path}`);
          value.path = `${url.pathname}${url.hash ? `/${hashToPath({hash: url.hash})}` : ''}`;
        }
        // Splits the pathname with the pathname in the config to ensure the
        // correct path is preserved on the URL. For example, a pathname of
        // `/dev/sc/page.php` with a config path of `/dev/sc` should net a
        // path of `/page.php` added onto the `basePath`.
        let topPath;
        if (this.historyConfig.absolutePaths) {
          topPath = `${this.basePath}${value.path}`;
        } else {
          topPath = `${this.basePath}${splitPath(value.path, this.configURL.pathname)}`;
        }
        topPath = topPath.replace(/\/{2,}/g, '/');
        updatePath({path: topPath}, true);

        this.trackCurrentIframeSrc(value.href);
        value.title && this.onTitleChange(value.title);
        break;
      }
      case EVENTS.IFRAME_RELOAD:
        this.generateIframeSource(true);
        break;
      case EVENTS.NOT_FOUND:
        this.setState({notFound: true});
        break;
      case EVENTS.OPEN_HELP_CENTER:
        document.dispatchEvent(new CustomEvent('openHelpCenter', {detail: value}));
        break;
      case EVENTS.READY:
        this.onReady(value);
        break;
      case EVENTS.SHELL_REDIRECT:
        this.props.router.trigger(getUpdatedPath(
          hashToPath({removePrecedingSlash: true}), value.path, this.props.config.hideTenant
        ), true);
        // The `discovery` flag was added as a workaround to an Analytics integration issue.
        // A better solution will soon follow (joao).
        value.discovery && this.generateIframeSource(true);
        break;
      case EVENTS.SHELL_SETTINGS:
        this.setProxiedConfiguration(value);
        break;
      case EVENTS.TRIGGER_NPS:
        document.dispatchEvent(new CustomEvent('triggerNps'));
        break;
      default:
        return;
    }
    this.props.onMessage(type, value);
  }

  /**
   * Settings handler to read/write/delete settings.
   * @param {Object} value Params and required Keys and settings.
   * @returns {Promise} Settings response.
   */
  async settingsHandler(value) {
    const {imsProfile, settingsService} = this.context;
    if (!imsProfile) {
      return this.queuedDataEvents.push(this.settingsHandler.bind(this, value));
    }
    const reqResponse = await settingsService.settingsHandler(value.data, value.method);
    this.sendMessage({
      type: Sandbox.EVENTS.EXC_SETTINGS,
      value: Object.assign(value, {reqResponse})
    });
  }

  async pulseHandler(value) {
    const {imsProfile, imsOrg} = this.context;
    if (!imsProfile) {
      return this.queuedDataEvents.push(this.pulseHandler.bind(this, value));
    }
    const requestData = {
      appId: this.props.config.appId,
      data: value.data,
      imsOrgId: imsOrg,
      method: value.method,
      userProfile: imsProfile
    };

    const pulseService = await this.getPulseService();
    const pulseResponse = await pulseService.sendNotifications(requestData);
    this.sendMessage({
      type: Sandbox.EVENTS.EXC_PULSE,
      value: Object.assign(value, {pulseResponse})
    });
  }

  setProxiedConfiguration(config) {
    // if digitalData is present, convert back to object before sending to metrics
    config.digitalData && (config.digitalData = JSON.parse(config.digitalData));
    this.metrics.event('Sandbox.setProxiedConfiguration', config);
    const validKeys = new Set([
      'coachMark',
      'customEnvLabel',
      'customSearch',
      'digitalData',
      'helpCenter',
      'hero',
      'modal',
      'nps',
      'onCustomButtonClick',
      'solution',
      'viewportTakeover'
    ]);
    Object.keys(config).forEach(key => {
      let value = config[key];
      switch (key) {
        case 'customButtons':
          config.onCustomButtonClick = value => this.sendMessage({
            type: scopedEvents[value.scope],
            value
          });
          break;
        case 'feedback':
          if (value.type === 'custom') {
            this.customFeedback = true;
          }
          break;
        case 'logoutUrl':
          this.context.setLogoutUrl(value);
          break;
        case 'preventDefaultCombos':
          this.eventObserver.setPreventDefaultCombos(value);
          break;
        case 'navConfig':
          this.cleanSidebarUrls(value.menu);
          break;
        case 'solution':
          config.hero = value;
          break;
        case 'spinner':
          this.metrics.event(value ? MetricsEvents.SPINNER_START : MetricsEvents.SPINNER_DONE);
          this.setState({spinner: value});
          break;
        case 'subOrgs': {
          const selectedSubOrg = value.selectedSubOrg;
          delete value.selectedSubOrg;
          this.context.setSubOrgs(value, selectedSubOrg);
          break;
        }
        case 'title':
          document.title = value;
          break;
        case 'unloadPromptMessage':
          this.unloadPromptMessage = value;
          break;
        case 'workspaces':
          value.map(val => this.updateWorkspaceURL(val));
          break;
        default: {
          if (!validKeys.has(key)) {
            // Delete any keys that do not match the above keys or keys in valid set so
            // Core state isn't filled with unneeded states and states don't get overridden
            delete config[key];
            this.metrics.info(`Sandbox: unknown shell configuration ${key}`);
          }
        }
      }
    });
    this.context.setConfigStates(config);
  }

  cleanUrl = (url) => {
    if (!url) {
      return '';
    }
    return /^https?:/.test(url) ? url : getPathWithoutTenant(`${this.getBase()}${url}`);
  };

  cleanSidebarUrls(menu) {
    for (let item of menu) {
      if (item.url) {
        item.url = item.absolutePath ? item.url : this.cleanUrl(item.url);
      } else if (item.children) { // nested children items
        this.cleanSidebarUrls(item.children);
      } else if (item.items) { // items nested in headers
        this.cleanSidebarUrls(item.items);
      }
    }
  }

  updateWorkspaceURL(workspace, parent) {
    const generateUid = (name, parentUid) => parentUid ? `${parentUid}_${name}` : name;

    if (!workspace.menu) {
      workspace.uid = generateUid(workspace.name, (parent || {}).uid);
      workspace.url = this.cleanUrl(workspace.url);
      return;
    }

    workspace.uid = generateUid(workspace.name, (parent || {}).uid);
    // Ensure that the workspace url is set to at least an empty string. Not
    // setting the url tells us that the workspace should not be added to the
    // workspace menu as the first item.
    workspace.url = this.cleanUrl(workspace.url);

    workspace.menu.forEach(ws => {
      ws.uid = generateUid(ws.name, workspace.uid);
      ws.url = this.cleanUrl(ws.url);
      ws.menu && this.updateWorkspaceURL(ws, workspace);
    });

    // Clicking on the top-level workspace opens the menu. If the workspace url
    // is set, we add it as the first item in the menu so that it can be
    // navigated to.
    workspace.url && workspace.menu.unshift({
      name: workspace.name,
      uid: generateUid(workspace.name, workspace.uid),
      url: workspace.url
    });
  }

  render() {
    const spinnerFrag = (
      <React.Fragment>
        <div className="spinner-overlay" />
        <Wait centered size="L" />
      </React.Fragment>
    );
    let {main, notFound, pageTimeout, spinner, src} = this.state;
    if (pageTimeout) {
      this.sandboxTimer.time('render', {message: 'NotFound'});
      return (
        <React.Suspense fallback={spinnerFrag}>
          <NotFound timeout={pageTimeout} />
        </React.Suspense>
      );
    }

    spinner = !src || spinner;

    let name = this.context.formatMessage({
      defaultMessage: 'Main Content',
      messageId: 'solutionIframe'
    });

    let sandboxProps = main ? {role: 'main'} : {};
    this.sandboxTimer.time('render', {applicationId: this.props.config.appId, main, spinner, src});
    return (
      <div className={classNames('exc-core-sandbox', {offset: !!this.props.config.sideNav})} {...sandboxProps}>
        {notFound &&
          <React.Suspense fallback={spinnerFrag}>
            <NotFound overlay={notFound} />
          </React.Suspense>}
        <Iframe
          appId={this.props.config.appId}
          name={name}
          onLoad={this.props.onLoad}
          onMessage={this.onMessage}
          onMount={this.onMount}
          src={src ? src.toString() : ''}
          style={{visiblity: this.state.loaded ? 'visible' : 'hidden'}}
          timeout={20000}
          title={name}
        />
        {spinner && spinnerFrag}
      </div>
    );
  }
}

Sandbox.contextType = CoreContext;

Sandbox.propTypes = {
  config: PropTypes.object.isRequired,
  onLoad: PropTypes.func,
  onMessage: PropTypes.func
};

Sandbox.defaultProps = {
  onLoad: function () {},
  onMessage: function () {}
};

Sandbox.EVENTS = {
  AUTH_EXPIRATION: 'AUTH_EXPIRATION',
  BLOCK_NAVIGATION: 'BLOCK_NAVIGATION',
  CAAS_LOAD: 'CAAS_LOAD',
  CAAS_READY: 'CAAS_READY',
  CAN_TAKEOVER: 'CAN_TAKEOVER',
  CLICK: 'CLICK',
  COACH_MARK_POSITIONED: 'COACH_MARK_POSITIONED',
  CONFIGURE: 'CONFIGURE',
  CUSTOM_FEEDBACK: 'CUSTOM_FEEDBACK',
  CUSTOM_HELP_BUTTON_CLICK: 'CUSTOM_HELP_BUTTON_CLICK',
  CUSTOM_HERO_CLICK: 'CUSTOM_HERO_CLICK',
  CUSTOM_PROFILE_BUTTON_CLICK: 'CUSTOM_PROFILE_BUTTON_CLICK',
  CUSTOM_PROFILE_CLICK: 'CUSTOM_PROFILE_CLICK',
  CUSTOM_SEARCH: 'CUSTOM_SEARCH',
  DATA_FULFILL: 'DATA_FULFILL',
  DATA_REQUEST: 'DATA_REQUEST',
  DONE: 'DONE',
  EXC_PULSE: 'EXC_PULSE',
  EXC_SETTINGS: 'EXC_SETTINGS',
  FOCUS: 'FOCUS',
  HERO_CLICK: 'HERO_CLICK',
  HISTORY: 'HISTORY',
  IFRAME_RELOAD: 'IFRAME_RELOAD',
  NOT_FOUND: 'NOT_FOUND',
  NPS: 'NPS',
  OPEN_HELP_CENTER: 'OPEN_HELP_CENTER',
  PING: 'PING',
  PONG: 'PONG',
  QUEUE: 'QUEUE',
  READY: 'READY',
  SHELL_REDIRECT: 'SHELL_REDIRECT',
  SHELL_SETTINGS: 'SHELL_SETTINGS',
  TRIGGER_NPS: 'TRIGGER_NPS',
  USER_INPUT: 'USER_INPUT'
};

Sandbox.EVENTS_THIRDPARTY = {
  AUTH_EXPIRATION: 'AUTH_EXPIRATION',
  CAN_TAKEOVER: 'CAN_TAKEOVER',
  CLICK: 'CLICK',
  CONFIGURE: 'CONFIGURE',
  CUSTOM_FEEDBACK: 'CUSTOM_FEEDBACK',
  CUSTOM_HELP_BUTTON_CLICK: 'CUSTOM_HELP_BUTTON_CLICK',
  CUSTOM_HERO_CLICK: 'CUSTOM_HERO_CLICK',
  CUSTOM_SEARCH: 'CUSTOM_SEARCH',
  DATA_FULFILL: 'DATA_FULFILL',
  DATA_REQUEST: 'DATA_REQUEST',
  DONE: 'DONE',
  FOCUS: 'FOCUS',
  HERO_CLICK: 'HERO_CLICK',
  HISTORY: 'HISTORY',
  IFRAME_RELOAD: 'IFRAME_RELOAD',
  OPEN_HELP_CENTER: 'OPEN_HELP_CENTER',
  PING: 'PING',
  PONG: 'PONG',
  QUEUE: 'QUEUE',
  READY: 'READY',
  SHELL_REDIRECT: 'SHELL_REDIRECT',
  SHELL_SETTINGS: 'SHELL_SETTINGS',
  USER_INPUT: 'USER_INPUT'
};

Sandbox.HISTORY_TYPES = {
  HASH: 'HASH',
  HISTORY: 'HISTORY',
  SERVER: 'SERVER'
};
